import { useCallback, useEffect, useMemo, useRef } from 'react';

type ExecuteFunction = (
	variables?: any,
	options?: { bypassCache?: boolean; skipLoadingState?: boolean },
) => void | Promise<void>;
type FetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';

export function usePollInterval(
	pollInterval: number | undefined,
	execute: ExecuteFunction,
	fetchPolicy?: FetchPolicy,
	isLazyQuery?: boolean,
	called?: boolean,
	isSSR?: boolean,
): { stopPolling: () => void } {
	if (pollInterval && fetchPolicy === 'cache-first') {
		throw new Error(
			'Queries that specify the cache-first fetchPolicy cannot also be polling queries.',
		);
	}

	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const stopPolling = useCallback(() => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
	}, []);

	const handlePoll = useCallback(() => {
		void execute(undefined, { bypassCache: true, skipLoadingState: true });
	}, [execute]);

	// For non-lazy queries: poll immediately when pollInterval > 0
	// For lazy queries: only poll after query has been called at least once
	const shouldStartPolling = useMemo(() => {
		if (isSSR || !pollInterval || pollInterval <= 0) {
			return false;
		}
		return !isLazyQuery || called;
	}, [isSSR, pollInterval, isLazyQuery, called]);

	useEffect(() => {
		// Clear any existing poll interval when conditions change
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}

		if (shouldStartPolling) {
			pollIntervalRef.current = setInterval(() => {
				handlePoll();
			}, pollInterval);
		}

		// Cleanup on unmount or when dependencies change
		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, [pollInterval, handlePoll, shouldStartPolling]);

	return { stopPolling };
}
